import React, { useCallback, useState } from 'react'

import MedicalRecordsService from 'services/medicalRecords'

import { Option, OptionsResponse, OTHER_COMPLAINT_OPTION_ID } from '../../types/option'

import { Badge, Typography } from '@telavita-core/react-design-kit'
import { Controller, useFormContext } from 'react-hook-form'
import { MedicalRecordFormData } from '../../medicalRecordSchema'
import { OtherOptionInput } from '../OtherOptionInput'
import { SearchMultiSelect } from '../SearchMultiSelect'
import * as S from './styles'

interface ComplaintsOption extends Option {
  id: number
  ciapCode: string
  description: string
}

const OTHERS_LABEL = 'Outros(as)'

export function ComplaintsSelect(): JSX.Element {
  const [complaintsOptions, setComplaintsOptions] = useState([])

  const {
    control,
    watch,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext<MedicalRecordFormData>()
  const complaintsSelected = watch('complaints') || []

  const handleChangeValue = (value: ComplaintsOption[]) =>
    setValue('complaints', value)

  const hasSelectedOptions = complaintsSelected.length > 0
  const hasOtherOptionInSelectedOptions = complaintsSelected.some(
    (op) => op.description === OTHERS_LABEL
  )

  const getComplaints = useCallback(
    async (
      query: string,
      page: number,
      isSearch = false
    ): Promise<OptionsResponse> => {
      const result = await MedicalRecordsService.listComplaints(query, page)
      const options = result.complaints.map((complaint) => {
        return {
          ...complaint,
          name: `${complaint.description} (CIAP ${complaint.ciapCode})`,
        } as ComplaintsOption
      })

      setComplaintsOptions((state) => {
        if (isSearch) return options
        return [...state, ...options]
      })

      return result
    },
    []
  )

  function handleRemoveSelectedOption(optionId: number) {
    const options = complaintsSelected.filter((op) => op.id !== optionId)
    if (optionId === OTHER_COMPLAINT_OPTION_ID) {
      resetField('other_complaint')
    }
    handleChangeValue(options)
  }

  return (
    <S.ComplaintsSelectContainer>
      <Controller
        control={control}
        name='complaints'
        render={({ field }) => (
          <SearchMultiSelect
            {...field}
            selectedOptions={complaintsSelected}
            onChange={field.onChange}
            options={complaintsOptions}
            label='Queixa do paciente *'
            description='Se não houver, digite e/ou selecione “nenhum(a)” para adicionar. Caso as
          opções não atendam, escolha "outros(as)".'
            floatingLabel='Digite e/ou selecione para adicionar'
            inputPlaceholder='Digite e/ou selecione para adicionar'
            emptyOptionsMessage='Nenhum resultado. Verifique sua digitação.'
            errorMessage={errors.complaints?.message}
            loadOptions={getComplaints}
          />
        )}
      />
      {hasSelectedOptions && (
        <S.SelectedOptionsContainer>
          {complaintsSelected.map((option) => (
            <Badge
              key={option.id}
              border
              rounded
              colorScheme='white'
              icon='Close'
              variant='lg'
              onClickIcon={() => handleRemoveSelectedOption(option.id)}
            >
              <Typography color='primary' variant='content2'>
                {option.name}
              </Typography>
            </Badge>
          ))}
        </S.SelectedOptionsContainer>
      )}
      {hasOtherOptionInSelectedOptions && (
        <Controller
          control={control}
          name='other_complaint'
          render={({ field, fieldState: { error } }) => (
            <OtherOptionInput
              {...field}
              errorMessage={error?.message}
              placeholder='Qual(is)?'
            />
          )}
        />
      )}
    </S.ComplaintsSelectContainer>
  )
}
